import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { MemberFormData } from '../types/member.types';
import { validateMemberForm } from '../validations';
import { membersService } from '../services/members.service';
import { mapMemberFormDataToApi } from '../mappers/member-mappers';
import { clearAllStaffCaches } from '../utils/staff-cache.utils';

import { OTHER_DEPARTMENT_CODE } from '@/lib/constants';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useDepartments } from '@/features/companies/hooks';
import { paths } from '@/config/paths';

interface ValidationError {
  field: string;
  message: string;
}

interface ValidationState {
  errors: ValidationError[];
  isValid: boolean;
  isSubmitting: boolean;
  touched: Set<string>;
}
export const useMemberForm = () => {
  const defaultFormData: MemberFormData = {
    id: '',
    lastName: '',
    firstName: '',
    lastNameYomi: '',
    firstNameYomi: '',
    tel: '',
    email: '',
    bucode: '',
    authorities: [],
    isSelectable: '',
    relatedStaffDepartmentCodes: [],
    code: '',
  };
  const [formData, setFormData] = useState<MemberFormData>(defaultFormData);
  const [memberData, setMemberData] = useState<MemberFormData | null>(null);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const [validationState, setValidationState] = useState<ValidationState>({
    errors: [],
    isValid: false,
    isSubmitting: false,
    touched: new Set<string>(),
  });
  const { departments, loading: isLoadingDepartments } = useDepartments();
  const { isLoading, setIsLoading } = useLoadingProgress();
  const navigate = useNavigate();
  // Use the comprehensive validation system
  const validation = useMemo(() => {
    return validateMemberForm(formData);
  }, [formData]);
  const hasFormChanges = useMemo(() => {
    const compareData = memberData ? memberData : defaultFormData;
    return JSON.stringify(formData) !== JSON.stringify(compareData);
  }, [formData, defaultFormData]);
  // Update validation state when form data changes
  useEffect(() => {
    setValidationState((prev) => ({
      ...prev,
      errors: validation.errors,
      isValid: validation.isValid,
    }));
  }, [validation]);
  const updateField = (
    field: keyof MemberFormData,
    value: MemberFormData[keyof MemberFormData]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateField = useCallback(
    (field: string) => {
      const validation = validateMemberForm(formData);
      const fieldError = validation.errors.find((error) => error.field === field);
      return fieldError ? fieldError.message : '';
    },
    [formData]
  );

  const isFieldValid = useCallback(
    (field: string) => {
      const validation = validateMemberForm(formData);
      return !validation.errors.some((error) => error.field === field);
    },
    [formData]
  );

  const getFieldError = useCallback(
    (field: string) => {
      return validateField(field);
    },
    [validateField]
  );

  const resetForm = useCallback(() => {
    setFormData({
      id: '',
      lastName: '',
      firstName: '',
      lastNameYomi: '',
      firstNameYomi: '',
      tel: '',
      email: '',
      bucode: '123',
      authorities: [],
      isSelectable: '',
      relatedStaffDepartmentCodes: [],
      code: '',
    });
    setValidationState({
      isSubmitting: false,
      isValid: false,
      touched: new Set<string>(),
      errors: [],
    });
  }, []);

  const registerMember = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true);
      setValidationState((prev) => ({ ...prev, isSubmitting: true }));
      const validation = validateMemberForm(formData);
      if (!validation.isValid) {
        setValidationState((prev) => ({ ...prev, isSubmitting: false }));
        setIsLoading(false);
        return {
          success: false,
          message: 'フォームにエラーがあります。入力内容を確認してください。',
        };
      }

      // Transform data to API format using mapper
      const apiData = mapMemberFormDataToApi(formData);

      // Call API
      await membersService.createMember(apiData);

      // Clear staff caches after successful member creation to sync dropdown data immediately.
      clearAllStaffCaches();

      setValidationState((prev) => ({ ...prev, isSubmitting: false }));
      setIsLoading(false);

      // Return true to indicate success - component will handle navigation
      return {
        success: true,
        message: '',
      };
    } catch (error) {
      const errorMessage =
        (error as Error)?.message || '登録に失敗しました。もう一度お試しください。';
      setValidationState((prev) => ({
        ...prev,
        isSubmitting: false,
        errors: [...prev.errors, { field: 'general', message: errorMessage }],
      }));
      setIsLoading(false);
      return {
        success: false,
        message: errorMessage,
      };
    }
  }, [formData]);

  const getMember = useCallback(
    async (memberId: string) => {
      try {
        setIsLoading(true);
        const member = await membersService.getStaffById(memberId);
        const departmetRelated = member.relatedStaffDepartmentCodes || [];
        const isOtherRelatedDepartment = departmetRelated.indexOf(OTHER_DEPARTMENT_CODE) >= 0;
        if (isOtherRelatedDepartment) {
          departmetRelated.splice(departmetRelated.indexOf(OTHER_DEPARTMENT_CODE), 1);
        }
        const mappedMemberData: MemberFormData = {
          id: member.id || '',
          lastName: member.lastName || '',
          firstName: member.firstName || '',
          lastNameYomi: member.lastNameYomi || '',
          firstNameYomi: member.firstNameYomi || '',
          tel: member.tel || '',
          email: member.email || '',
          bucode: member.bucode ? String(member.bucode) : '',
          authorities: member.authorities || [],
          isSelectable: member.isSelectable ? String(member.isSelectable) : '',
          relatedStaffDepartmentCodes: departmetRelated,
          isOtherRelatedDepartment: isOtherRelatedDepartment,
          code: member.code ? String(member.code) : '',
        };
        setIsDeleted(member.isDeleted || false);
        setMemberData(mappedMemberData);
        setFormData(mappedMemberData);
        setIsLoading(false);
        return member;
      } catch (error) {
        console.error('Error fetching member:', error);
        navigate(paths.notFound.path);
        setIsLoading(false);
        return null;
      }
    },
    [setIsLoading]
  );

  const updateMember = useCallback(
    async (memberId: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        setValidationState((prev) => ({ ...prev, isSubmitting: true }));

        // Validate form before updating
        const validation = validateMemberForm(formData);
        if (!validation.isValid) {
          setValidationState((prev) => ({ ...prev, isSubmitting: false }));
          setIsLoading(false);
          return false;
        }

        // Transform data to API format and update
        const apiData = mapMemberFormDataToApi(formData);
        await membersService.updateStaffById(memberId, apiData);

        // Clear staff caches right after successful role update to force fresh loads across screens.
        clearAllStaffCaches();

        // Update the member data state to reflect the changes
        setMemberData(formData);

        setValidationState((prev) => ({ ...prev, isSubmitting: false }));
        setIsLoading(false);
        return true;
      } catch (error) {
        console.error('Error updating member:', error);
        setValidationState((prev) => ({ ...prev, isSubmitting: false }));
        setIsLoading(false);
        return false;
      }
    },
    [formData, setIsLoading]
  );

  const deleteMember = useCallback(
    async (id: string): Promise<{ success: boolean; message: string }> => {
      try {
        setIsLoading(true);
        // Call API to delete member
        await membersService.deleteMember(id);

        // Clear staff caches after successful member deletion to sync dropdown data immediately.
        clearAllStaffCaches();

        setIsLoading(false);
        return {
          success: true,
          message: '',
        };
      } catch (error) {
        setIsLoading(false);
        return {
          success: false,
          message: (error as Error)?.message,
        };
      }
    },
    []
  );
  const restoreMember = useCallback(async (memberId: string) => {
    try {
      setIsLoading(true);
      await membersService.restoreMember(memberId);

      // Clear staff caches after successful member restoration to sync dropdown data immediately.
      clearAllStaffCaches();

      return {
        success: true,
        message: '',
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error)?.message,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reinviteMember = useCallback(async (email: string) => {
    try {
      setIsLoading(true);
      await membersService.reinviteMember(email);
      return {
        success: true,
        message: '',
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error)?.message,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    formData,
    memberData,
    validationState,
    departments,
    isLoadingDepartments,
    isLoading,
    hasFormChanges,
    isDeleted,
    updateField,
    getFieldError,
    isFieldValid,
    resetForm,
    registerMember,
    getMember,
    updateMember,
    deleteMember,
    restoreMember,
    reinviteMember,
  };
};
